import React from 'react';
import '../styles/Features.css';

function Features() {
  const featureCategories = [
    {
      title: 'Core Screening Features',
      features: [
        {
          icon: '⚙️',
          title: 'AI Resume Parsing',
          description:
            'Automatically extract skills, experience, education, and certifications from PDF, DOC, and DOCX files. Multi-language support with intelligent normalization of job titles.',
        },
        {
          icon: '📋',
          title: 'Job Description Intelligence',
          description:
            'Upload or paste job descriptions and let AI break them down into must-have vs good-to-have skills, experience weightage, and detect JD bias automatically.',
        },
        {
          icon: '📊',
          title: 'AI Match Score',
          description:
            'Get comprehensive hiring fit scores (0-100) based on skill match percentage, experience relevance, and industry similarity for accurate candidate evaluation.',
        },
        {
          icon: '⭐',
          title: 'Ranking & Shortlisting',
          description:
            'Rank thousands of resumes in seconds with automated shortlisting based on customizable thresholds, while maintaining manual override capabilities for recruiters.',
        },
      ],
    },
    {
      title: 'Smart AI Features',
      features: [
        {
          icon: '🧠',
          title: 'Contextual Skill Understanding',
          description:
            'Goes beyond keyword matching to understand skill context. Differentiates between React and React Native, estimates skill depth (Beginner/Intermediate/Advanced), and detects skill decay.',
        },
        {
          icon: '📈',
          title: 'Experience Quality Score',
          description:
            'Analyzes career growth patterns, detects progression from Junior to Senior to Lead roles, and identifies potential experience inflation or job hopping patterns.',
        },
        {
          icon: '🔍',
          title: 'Resume Authenticity Signals',
          description:
            'AI flags overused buzzwords, template-based resumes, and copy-paste projects with confidence scores to help you identify genuine candidates.',
        },
      ],
    },
    {
      title: 'Recruiter Productivity',
      features: [
        {
          icon: '⚡',
          title: 'Bulk Hiring Mode',
          description:
            'Upload and screen 1000+ resumes at once. Filter by location, salary expectations, and notice period. Send one-click bulk rejection emails to save time.',
        },
        {
          icon: '🤖',
          title: 'AI Recruiter Copilot',
          description:
            'Auto-generated candidate summaries, strength and risk analysis, and clear explanations of "why this candidate" to build trust in AI decisions.',
        },
        {
          icon: '✍️',
          title: 'AI Interview Question Generator',
          description:
            'Generate personalized interview questions based on resume gaps and JD requirements, with difficulty scaling for Junior to Senior level positions.',
        },
      ],
    },
    {
      title: 'Bias & Compliance',
      features: [
        {
          icon: '⚖️',
          title: 'Bias-Aware Screening',
          description:
            'Blind resume mode to hide name, gender, and college information. Fairness metrics dashboard with compliance for EEOC (US), GDPR, and India hiring norms.',
        },
        {
          icon: '🔓',
          title: 'Explainable AI',
          description:
            'Every score includes detailed reason breakdown and skill-wise contribution analysis. No black box decisions—complete transparency in AI recommendations.',
        },
      ],
    },
    {
      title: 'Analytics & Intelligence',
      features: [
        {
          icon: '📉',
          title: 'Hiring Funnel Analytics',
          description:
            'Track drop-off points, resume-to-interview-to-offer conversion rates, and time-to-hire analytics to optimize your recruitment process.',
        },
        {
          icon: '🗺️',
          title: 'Talent Pool Insights',
          description:
            'Visualize skill heatmaps across applicants, analyze salary vs skill trends, and identify location-wise talent availability for strategic hiring.',
        },
        {
          icon: '🔮',
          title: 'Predictive Hiring',
          description:
            'Predict candidate offer acceptance probability and identify early attrition risks based on historical data and AI-powered pattern recognition.',
        },
      ],
    },
    {
      title: 'Integrations',
      features: [
        {
          icon: '🔌',
          title: 'ATS Integrations',
          description:
            'Seamlessly integrate with popular ATS platforms including Greenhouse, Lever, Workday, Zoho Recruit, and custom ATS solutions via our flexible API.',
        },
        {
          icon: '🌐',
          title: 'Job Board Connections',
          description:
            'Direct integration with LinkedIn, Indeed, Naukri, and company career pages to streamline your candidate sourcing and screening workflow.',
        },
      ],
    },
    {
      title: 'AI-Native Power Features',
      features: [
        {
          icon: '🧬',
          title: 'Candidate DNA Profile',
          description:
            'AI creates long-term candidate profiles analyzing learning velocity, role adaptability, and tech evolution trends that are reusable across multiple roles.',
        },
        {
          icon: '🕵️',
          title: 'Resume vs Reality Check',
          description:
            'Compare resume skills with actual GitHub activity, StackOverflow contributions, and Kaggle profiles (opt-in only) for authentic skill verification.',
        },
        {
          icon: '🤖',
          title: 'Autonomous Screening Agent',
          description:
            'Deploy AI agents that autonomously screen resumes, shortlist candidates, schedule interviews, and send feedback emails with minimal human involvement.',
        },
      ],
    },
  ];

  return (
    <section id="features" className="features section">
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center">
          <h2 className="section-title">Powerful Features for Modern Recruiting</h2>
          <p className="section-subtitle">
            Everything you need to screen, rank, and hire the best candidates with AI-powered
            precision and fairness.
          </p>
        </div>

        {/* Feature Categories */}
        {featureCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="feature-category">
            <h3 className="category-title">{category.title}</h3>
            <div className="feature-grid">
              {category.features.map((feature, featureIndex) => (
                <div key={featureIndex} className="feature-card card">
                  <div className="feature-icon">{feature.icon}</div>
                  <h4 className="feature-title">{feature.title}</h4>
                  <p className="feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Features;
